<template>
    <div class="grade-management">
        <!-- 筛选器 -->
        <el-card class="filter-card">
            <el-form :inline="true" :model="filters" class="filter-form">
                <el-form-item label="学期">
                    <el-select v-model="filters.semesterId" placeholder="选择学期" @change="fetchGrades"
                        style="width: 200px">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="考试类型">
                    <el-select v-model="filters.examType" placeholder="选择考试" clearable @change="fetchGrades"
                        style="width: 160px" allow-create filterable>
                        <el-option-group label="常规考试">
                            <el-option label="月考1" value="monthly_1" />
                            <el-option label="期中考试" value="midterm" />
                            <el-option label="月考2" value="monthly_2" />
                            <el-option label="期末考试" value="final" />
                        </el-option-group>
                        <el-option-group label="高三模拟">
                            <el-option label="一模" value="mock_1" />
                            <el-option label="二模" value="mock_2" />
                            <el-option label="三模" value="mock_3" />
                        </el-option-group>
                        <el-option-group label="其他">
                            <el-option label="月考3" value="monthly_3" />
                            <el-option label="诊断性考试" value="diagnostic" />
                            <el-option label="联合模拟" value="joint_mock" />
                        </el-option-group>
                    </el-select>
                </el-form-item>
                <el-form-item label="年级">
                    <el-select v-model="filters.grade" placeholder="选择年级" clearable @change="fetchGrades"
                        style="width: 100px">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
                <el-form-item label="搜索">
                    <el-input v-model="filters.keyword" @input="updateKeyword" placeholder="学号/姓名" clearable
                        style="width: 120px" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-select v-model="filters.sortBy" style="width: 120px">
                        <el-option label="总分降序" value="total_desc" />
                        <el-option label="总分升序" value="total_asc" />
                        <el-option label="按班级" value="class" />
                        <el-option label="按学号" value="student_id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="显示科目">
                    <el-select v-model="displaySubjects" multiple collapse-tags collapse-tags-tooltip placeholder="选择科目"
                        style="width: 200px">
                        <el-option v-for="sub in allSubjects" :key="sub" :label="sub" :value="sub" />
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" @click="showAddStudentDialog">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        添加学生
                    </el-button>
                    <el-button @click="showImportStudentDialog">
                        <el-icon>
                            <Upload />
                        </el-icon>
                        批量导入学生
                    </el-button>
                    <el-button @click="showImportGradeDialog">
                        <el-icon>
                            <DocumentAdd />
                        </el-icon>
                        批量导入成绩
                    </el-button>
                    <el-popconfirm title="确定删除当前筛选条件下的所有成绩？此操作不可恢复！" confirm-button-text="确定删除" cancel-button-text="取消"
                        confirm-button-type="danger" @confirm="batchDeleteGrades">
                        <template #reference>
                            <el-button type="danger" :disabled="!filters.semesterId || !filters.examType">
                                批量删除成绩
                            </el-button>
                        </template>
                    </el-popconfirm>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 学生成绩表格 -->
        <el-card class="table-card">
            <template #header>
                <div class="card-header">
                    <span>成绩列表</span>
                    <el-button type="primary" text @click="showGradeEntryDialog" :disabled="!selectedStudent">
                        录入成绩
                    </el-button>
                </div>
            </template>

            <el-table :data="sortedStudents" v-loading="loading" stripe @row-click="selectStudent" highlight-current-row
                height="600" :row-key="row => row.id">
                <el-table-column prop="student_id" label="学号" width="120" />
                <el-table-column prop="name" label="姓名" width="100" />
                <el-table-column prop="class_name" label="班级" width="100" />
                <el-table-column prop="grade" label="年级" width="80" />
                <el-table-column v-for="sub in displaySubjects" :key="sub" :label="getSubjectLabel(sub)" width="90">
                    <template #default="{ row }">
                        {{ getGrade(row.id, sub) }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="280" fixed="right">
                    <template #default="{ row }">
                        <div class="action-buttons">
                            <el-button size="small" type="primary" plain @click.stop="viewAllGrades(row)">查看</el-button>
                            <el-button size="small" @click.stop="editStudent(row)">编辑</el-button>
                            <el-button size="small" type="success" plain
                                @click.stop="openGradeEntry(row)">录入</el-button>
                            <el-popconfirm title="确定删除此学生？" @confirm="deleteStudent(row.id)">
                                <template #reference>
                                    <el-button size="small" type="danger" plain @click.stop>删除</el-button>
                                </template>
                            </el-popconfirm>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- 添加/编辑学生对话框 -->
        <el-dialog v-model="studentDialogVisible" :title="isEditStudent ? '编辑学生' : '添加学生'" width="500px">
            <el-form :model="studentForm" label-width="100px" :rules="studentRules" ref="studentFormRef">
                <el-form-item label="学号" prop="student_id">
                    <el-input v-model="studentForm.student_id"
                        placeholder="如：25090101（年份+月份+班级+序号）"
                        :disabled="isEditStudent" />
                    <div class="field-hint" v-if="!isEditStudent">格式：25（入学年份）+ 09（月份）+ 01（班级）+ 01（序号）</div>
                </el-form-item>
                <el-form-item label="姓名" prop="name">
                    <el-input v-model="studentForm.name" placeholder="请输入姓名" />
                </el-form-item>
                <el-form-item label="班级">
                    <el-input v-model="studentForm.class_name" placeholder="如: 1班" />
                </el-form-item>
                <el-form-item label="年级" prop="grade" v-if="!isEditStudent">
                    <el-select v-model="studentForm.grade" placeholder="选择年级" style="width: 100%">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="studentDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitStudent" :loading="submitting">确定</el-button>
            </template>
        </el-dialog>

        <!-- 批量导入学生对话框 -->
        <el-dialog v-model="importStudentDialogVisible" title="批量导入学生" width="600px">
            <el-form :model="importStudentForm" label-width="100px">
                <el-form-item label="学期" required>
                    <el-select v-model="importStudentForm.semesterId" placeholder="选择学期" style="width: 100%">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="年级" required>
                    <el-select v-model="importStudentForm.grade" placeholder="选择年级" style="width: 100%">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>
            </el-form>

            <el-tabs v-model="importStudentTab" class="import-tabs">
                <!-- Excel 导入 -->
                <el-tab-pane label="Excel导入" name="excel">
                    <div class="import-section">
                        <el-button type="primary" plain @click="downloadStudentTemplate" class="download-btn">
                            <el-icon>
                                <Download />
                            </el-icon>
                            下载模板
                        </el-button>
                        <el-upload ref="studentUploadRef" :auto-upload="false" :limit="1" accept=".xlsx,.xls"
                            :on-change="handleStudentFileChange" :on-remove="() => studentFile = null" drag
                            class="upload-area">
                            <el-icon class="el-icon--upload">
                                <Upload />
                            </el-icon>
                            <div class="el-upload__text">
                                拖拽文件到此处，或 <em>点击上传</em>
                            </div>
                            <template #tip>
                                <div class="el-upload__tip">
                                    Excel需包含"学号"和"姓名"列，可选"班级"列
                                </div>
                            </template>
                        </el-upload>
                    </div>
                </el-tab-pane>

                <!-- 粘贴导入 -->
                <el-tab-pane label="粘贴导入" name="paste">
                    <div class="import-section">
                        <div class="paste-tip">
                            <el-alert type="info" :closable="false" show-icon>
                                <template #title>
                                    格式说明：每行一个学生，使用 Tab 或逗号分隔
                                </template>
                                <template #default>
                                    格式：学号, 姓名, 班级（可选）<br />
                                    学号格式：<strong>25090101</strong>（年份+月份+班级+序号）<br />
                                    例如：<br />
                                    25090101, 张三, 1班<br />
                                    25090102, 李四, 1班
                                </template>
                            </el-alert>
                        </div>
                        <el-input v-model="importStudentForm.pasteContent" type="textarea" :rows="10"
                            placeholder="在此粘贴学生数据..." class="paste-textarea" />
                        <div class="paste-preview" v-if="parsedStudents.length > 0">
                            <el-tag type="success">已解析 {{ parsedStudents.length }} 名学生</el-tag>
                            <el-table :data="parsedStudents.slice(0, 5)" size="small" max-height="150">
                                <el-table-column prop="student_id" label="学号" />
                                <el-table-column prop="name" label="姓名" />
                                <el-table-column prop="class_name" label="班级" />
                            </el-table>
                            <div v-if="parsedStudents.length > 5" class="more-hint">
                                ... 还有 {{ parsedStudents.length - 5 }} 条数据
                            </div>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <template #footer>
                <el-button @click="importStudentDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="importStudents" :loading="importing">导入</el-button>
            </template>
        </el-dialog>

        <!-- 批量导入成绩对话框 -->
        <el-dialog v-model="importGradeDialogVisible" title="批量导入成绩" width="660px">
            <el-form :model="importGradeForm" label-width="100px">
                <el-form-item label="学期" required>
                    <el-select v-model="importGradeForm.semesterId" placeholder="选择学期" style="width: 100%">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="考试类型" required>
                    <el-select v-model="importGradeForm.examType" placeholder="选择或输入考试类型"
                        style="width: 100%" allow-create filterable
                        @change="onImportExamTypeChange">
                        <el-option-group label="常规考试">
                            <el-option label="月考1" value="monthly_1" />
                            <el-option label="期中考试" value="midterm" />
                            <el-option label="月考2" value="monthly_2" />
                            <el-option label="期末考试" value="final" />
                        </el-option-group>
                        <el-option-group label="高三模拟">
                            <el-option label="一模" value="mock_1" />
                            <el-option label="二模" value="mock_2" />
                            <el-option label="三模" value="mock_3" />
                        </el-option-group>
                        <el-option-group label="其他">
                            <el-option label="月考3" value="monthly_3" />
                            <el-option label="诊断性考试" value="diagnostic" />
                            <el-option label="联合模拟" value="joint_mock" />
                        </el-option-group>
                    </el-select>
                    <div class="field-hint">可直接输入自定义考试名称，如「二模」「三模」「联考」</div>
                </el-form-item>
                <el-form-item label="各科满分">
                    <el-alert type="warning" :closable="false" show-icon style="margin-bottom: 10px; width: 100%">
                        <template #title>每次考试的满分可能不同，请根据本次考试实际满分设置</template>
                        <template #default>满分设置优先级高于 Excel 文件中的满分列，无需在 Excel 中单独添加满分列</template>
                    </el-alert>
                    <div class="subject-totals-grid">
                        <div v-for="sub in subjects" :key="sub" class="total-grid-item">
                            <span class="total-label">{{ sub }}</span>
                            <el-input-number v-model="importGradeForm.subjectTotals[sub]" :min="1" :max="300"
                                size="small" style="width: 90px" />
                        </div>
                    </div>
                </el-form-item>
                <el-tabs v-model="importGradeTab" class="import-tabs">
                    <el-tab-pane label="Excel导入" name="excel">
                        <div class="import-section">
                            <el-button type="primary" plain :icon="Download" @click="downloadGradeTemplate"
                                class="download-btn">
                                下载模板
                            </el-button>
                            <el-upload ref="gradeUploadRef" :auto-upload="false" :limit="1" accept=".xlsx,.xls"
                                :on-change="handleGradeFileChange" :on-remove="() => gradeFile = null" drag
                                class="upload-area">
                                <el-icon class="el-icon--upload"><Upload /></el-icon>
                                <div class="el-upload__text">拖拽 Excel 文件到此处，或 <em>点击上传</em></div>
                                <template #tip>
                                    <div class="el-upload__tip">支持 .xlsx, .xls 格式，第一行必须为表头，必须包含「学号」列</div>
                                </template>
                            </el-upload>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane label="粘贴导入" name="paste">
                        <div class="import-section">
                            <el-alert type="info" :closable="false" show-icon style="margin-bottom: 10px">
                                <template #title>从 Excel 中复制数据后粘贴到下方文本框</template>
                                <template #default>
                                    第一行必须为表头，学号列名须为「学号」<br />
                                    示例表头：学号 &nbsp;语文 &nbsp;数学 &nbsp;英语 &nbsp;物理 &nbsp;化学
                                </template>
                            </el-alert>
                            <el-input v-model="pasteGradeContent" type="textarea" :rows="8"
                                placeholder="学号&#9;语文&#9;数学&#9;英语&#9;物理&#9;化学..." class="paste-textarea" />

                            <div v-if="parsedGrades.length > 0" class="paste-preview">
                                <div class="preview-title">解析结果预览（前5条）</div>
                                <el-table :data="parsedGrades.slice(0, 5)" size="small" border>
                                    <el-table-column prop="student_id" label="学号" width="110" />
                                    <el-table-column v-for="sub in subjects" :key="sub" :prop="sub" :label="sub" />
                                </el-table>
                                <div v-if="parsedGrades.length > 5" class="more-hint">
                                    ... 还有 {{ parsedGrades.length - 5 }} 条数据
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </el-form>
            <template #footer>
                <el-button @click="importGradeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="importGrades" :loading="importing">导入</el-button>
            </template>
        </el-dialog>

        <!-- 成绩录入对话框 -->
        <el-dialog v-model="gradeDialogVisible" title="录入成绩" width="600px">
            <div v-if="gradeEntryStudent" class="grade-entry-header">
                <span>学生：{{ gradeEntryStudent.name }} ({{ gradeEntryStudent.student_id }})</span>
            </div>
            <el-form :model="gradeForm" label-width="100px">
                <el-form-item label="考试类型" required>
                    <el-select v-model="gradeForm.examType" placeholder="选择或输入考试类型" style="width: 100%"
                        allow-create filterable @change="loadStudentExamGrades">
                        <el-option-group label="常规考试">
                            <el-option label="月考1" value="monthly_1" />
                            <el-option label="期中考试" value="midterm" />
                            <el-option label="月考2" value="monthly_2" />
                            <el-option label="期末考试" value="final" />
                        </el-option-group>
                        <el-option-group label="高三模拟">
                            <el-option label="一模" value="mock_1" />
                            <el-option label="二模" value="mock_2" />
                            <el-option label="三模" value="mock_3" />
                        </el-option-group>
                        <el-option-group label="其他">
                            <el-option label="月考3" value="monthly_3" />
                            <el-option label="诊断性考试" value="diagnostic" />
                            <el-option label="联合模拟" value="joint_mock" />
                        </el-option-group>
                    </el-select>
                </el-form-item>
                <el-divider />
                <el-row :gutter="16" v-for="subject in subjects" :key="subject">
                    <el-col :span="12">
                        <el-form-item :label="subject">
                            <el-input-number v-model="gradeForm.scores[subject]" :min="0"
                                :max="gradeForm.totals[subject]" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="满分">
                            <el-input-number v-model="gradeForm.totals[subject]" :min="1" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <template #footer>
                <el-button @click="gradeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitGrades" :loading="submitting">保存</el-button>
            </template>
        </el-dialog>

        <!-- 导入错误详情弹窗 -->
        <el-dialog v-model="importErrorsVisible" title="导入错误详情" width="600px">
            <el-alert type="warning" :closable="false" show-icon style="margin-bottom: 12px">
                <template #title>以下 {{ importErrors.length }} 条记录导入失败，其他记录已成功导入</template>
            </el-alert>
            <el-scrollbar max-height="350px">
                <div v-for="(err, idx) in importErrors" :key="idx" class="error-item">
                    <el-icon color="#e6a23c"><Warning /></el-icon>
                    <span>{{ err }}</span>
                </div>
            </el-scrollbar>
            <template #footer>
                <el-button type="primary" @click="importErrorsVisible = false">知道了</el-button>
            </template>
        </el-dialog>

        <!-- 查看学生所有成绩对话框 -->
        <el-dialog v-model="allGradesDialogVisible"
            :title="allGradesStudent ? `${allGradesStudent.name} 的所有成绩` : '成绩详情'" width="900px">
            <el-table :data="allGradesData" stripe :header-cell-style="{ textAlign: 'center' }"
                :cell-style="{ textAlign: 'center' }">
                <el-table-column prop="semester_name" label="学期" width="180" align="center" />
                <el-table-column prop="exam_type_label" label="考试类型" width="120" align="center" />
                <el-table-column v-for="sub in subjects" :key="sub" :prop="sub" :label="sub" width="80"
                    align="center" />
                <el-table-column label="总分" width="80" align="center">
                    <template #default="{ row }">
                        <strong>{{ row.total }}</strong>
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { Plus, Upload, DocumentAdd, Download, Warning } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, studentApi, gradeApi } from '@/api'

// 状态
const loading = ref(false)
const submitting = ref(false)
const importing = ref(false)
const semesters = ref([])
const students = ref([])
const grades = ref([])
const selectedStudent = ref(null)

// 导入错误详情
const importErrors = ref([])
const importErrorsVisible = ref(false)

// 查看所有成绩
const allGradesDialogVisible = ref(false)
const allGradesStudent = ref(null)
const allGradesData = ref([])

// 筛选条件
const filters = reactive({
    semesterId: null,
    examType: null,
    grade: null,
    keyword: '',
    sortBy: 'total_desc'
})

// 计算学生总分 - 使用索引优化
const gradesIndex = computed(() => {
    // 构建索引: { studentId: { subject: grade } }
    const index = {}
    grades.value.forEach(g => {
        if (!index[g.student_id]) index[g.student_id] = {}
        index[g.student_id][g.subject] = g
    })
    return index
})

const getStudentTotal = (student) => {
    const studentGrades = gradesIndex.value[student.id] || {}
    let total = 0
    subjects.forEach(sub => {
        const g = studentGrades[sub]
        if (g && typeof g.score === 'number') total += g.score
    })
    return total
}

// 预计算总分缓存
const studentTotals = computed(() => {
    const map = {}
    students.value.forEach(s => {
        map[s.id] = getStudentTotal(s)
    })
    return map
})

// 防抖搜索关键词
const debouncedKeyword = ref('')
let debounceTimer = null
const updateKeyword = (val) => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        debouncedKeyword.value = val
    }, 200)
}

// 排序和搜索后的学生列表
const sortedStudents = computed(() => {
    let list = [...students.value]

    // 搜索过滤（使用防抖后的关键词）
    const kw = debouncedKeyword.value.toLowerCase()
    if (kw) {
        list = list.filter(s =>
            s.student_id.toLowerCase().includes(kw) ||
            s.name.toLowerCase().includes(kw)
        )
    }

    // 排序（使用缓存的总分）
    switch (filters.sortBy) {
        case 'total_desc':
            list.sort((a, b) => (studentTotals.value[b.id] || 0) - (studentTotals.value[a.id] || 0))
            break
        case 'total_asc':
            list.sort((a, b) => (studentTotals.value[a.id] || 0) - (studentTotals.value[b.id] || 0))
            break
        case 'class':
            list.sort((a, b) => (a.class_name || '').localeCompare(b.class_name || ''))
            break
        case 'student_id':
            list.sort((a, b) => a.student_id.localeCompare(b.student_id))
            break
    }

    return list
})

// 学生表单
const studentDialogVisible = ref(false)
const isEditStudent = ref(false)
const editingStudentId = ref(null)
const studentFormRef = ref(null)
const studentForm = reactive({
    student_id: '',
    name: '',
    class_name: '',
    grade: '高一'
})
const studentRules = {
    student_id: [{ required: true, message: '请输入学号', trigger: 'blur' }],
    name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
    grade: [{ required: true, message: '请选择年级', trigger: 'change' }]
}

// 导入学生表单
const importStudentDialogVisible = ref(false)
const importStudentTab = ref('excel')
const studentUploadRef = ref(null)
const studentFile = ref(null)
const importStudentForm = reactive({
    semesterId: null,
    grade: '高一',
    pasteContent: ''
})

// 解析粘贴内容
const parsedStudents = computed(() => {
    if (!importStudentForm.pasteContent.trim()) return []
    const lines = importStudentForm.pasteContent.trim().split('\n')
    const result = []
    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        // 支持Tab或逗号分隔
        const parts = trimmed.includes('\t') ? trimmed.split('\t') : trimmed.split(/[,，]/)
        if (parts.length >= 2) {
            result.push({
                student_id: parts[0].trim(),
                name: parts[1].trim(),
                class_name: parts[2]?.trim() || ''
            })
        }
    }
    return result
})

// 导入成绩表单
const importGradeDialogVisible = ref(false)
const importGradeTab = ref('excel')
const gradeUploadRef = ref(null)
const gradeFile = ref(null)
const pasteGradeContent = ref('')
const importGradeForm = reactive({
    semesterId: null,
    examType: 'monthly_1',
    subjectTotals: {
        '语文': 120,
        '数学': 120,
        '英语': 120,
        '物理': 100,
        '化学': 100,
        '生物': 100,
        '政治': 100,
        '历史': 100,
        '地理': 100
    }
})

// 解析粘贴成绩内容
const parsedGrades = computed(() => {
    if (!pasteGradeContent.value.trim()) return []
    const lines = pasteGradeContent.value.trim().split('\n')
    if (lines.length < 1) return []

    // 尝试解析第一行作为表头
    const headerLine = lines[0].trim()
    const headerParts = headerLine.includes('\t') ? headerLine.split('\t') : headerLine.split(/[,，]/)

    // 寻找学号列
    const studentIdIndex = headerParts.findIndex(p => p.includes('学号') || p.toLowerCase().includes('id'))
    if (studentIdIndex === -1 && headerParts.length < 2) return []

    const result = []
    // 从第二行开始是数据（如果第一行被识别为表头）
    // 如果第一行全是数字，可能没有表头
    const hasHeader = isNaN(headerParts[0]) || headerParts[0].length > 10
    const startLine = hasHeader ? 1 : 0
    const currentHeader = hasHeader ? headerParts : ['学号', ...subjects]

    for (let i = startLine; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue
        const parts = line.includes('\t') ? line.split('\t') : line.split(/[,，]/)

        const item = { student_id: parts[studentIdIndex === -1 ? 0 : studentIdIndex].trim() }

        // 匹配科目
        subjects.forEach(sub => {
            const subIndex = currentHeader.findIndex(h => h.includes(sub))
            if (subIndex !== -1 && parts[subIndex]) {
                item[sub] = parseFloat(parts[subIndex]) || 0
            }
        })
        result.push(item)
    }
    return result
})

// 成绩录入表单
const gradeDialogVisible = ref(false)
const gradeEntryStudent = ref(null)
const allSubjects = ['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理']
const subjects = ['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理']  // 全部科目

// 显示的科目（用户可选择，默认全部显示）
const displaySubjects = ref(['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理'])

const gradeForm = reactive({
    examType: 'monthly_1',
    scores: {},
    totals: {}
})

// 初始化成绩表单
const initGradeForm = () => {
    gradeForm.scores = {}
    gradeForm.totals = {}
    subjects.forEach(s => {
        gradeForm.scores[s] = null
        gradeForm.totals[s] = 100
    })
}

// 获取成绩辅助函数 - 使用索引优化
const getGrade = (studentId, subject) => {
    const studentGrades = gradesIndex.value[studentId]
    const grade = studentGrades ? studentGrades[subject] : null
    return grade ? grade.score : '-'
}

// 获取科目表头标签（显示满分）
const getSubjectLabel = (subject) => {
    // 从成绩数据中找到该科目的满分
    const grade = grades.value.find(g => g.subject === subject)
    const total = grade ? grade.total_score : 120
    return `${subject}[${total}]`
}

// 数据获取
const fetchSemesters = async () => {
    try {
        semesters.value = await semesterApi.getAll()
        if (semesters.value.length > 0 && !filters.semesterId) {
            // 默认选中当前学期或第一个
            const current = semesters.value.find(s => s.is_active)
            filters.semesterId = current ? current.id : semesters.value[0].id
        }
    } catch (e) {
        console.error(e)
    }
}

const fetchStudents = async () => {
    loading.value = true
    console.time('[Performance] fetchStudents API')
    try {
        const params = { semester_id: filters.semesterId }
        if (filters.grade) params.grade = filters.grade
        students.value = await studentApi.getAll(params)
        console.timeEnd('[Performance] fetchStudents API')
    } catch (e) {
        console.timeEnd('[Performance] fetchStudents API')
        console.error(e)
    } finally {
        loading.value = false
    }
}

const fetchGrades = async () => {
    console.time('[Performance] fetchGrades total')
    console.time('[Performance] fetchStudents in fetchGrades')
    await fetchStudents()
    console.timeEnd('[Performance] fetchStudents in fetchGrades')
    if (!filters.semesterId) {
        console.timeEnd('[Performance] fetchGrades total')
        return
    }
    console.time('[Performance] fetchGrades API')
    try {
        const params = { semester_id: filters.semesterId }
        if (filters.examType) params.exam_type = filters.examType
        grades.value = await gradeApi.getAll(params)
        console.timeEnd('[Performance] fetchGrades API')
    } catch (e) {
        console.timeEnd('[Performance] fetchGrades API')
        console.error(e)
    }
    console.timeEnd('[Performance] fetchGrades total')
    console.log('[Performance] students count:', students.value.length, 'grades count:', grades.value.length)
}

// 学生操作
const showAddStudentDialog = () => {
    isEditStudent.value = false
    editingStudentId.value = null
    Object.assign(studentForm, { student_id: '', name: '', class_name: '', grade: '高一' })
    studentDialogVisible.value = true
}

const editStudent = (row) => {
    isEditStudent.value = true
    editingStudentId.value = row.id
    Object.assign(studentForm, {
        student_id: row.student_id,
        name: row.name,
        class_name: row.class_name,
        grade: row.grade
    })
    studentDialogVisible.value = true
}

const submitStudent = async () => {
    if (!studentFormRef.value) return
    await studentFormRef.value.validate(async (valid) => {
        if (!valid) return
        submitting.value = true
        try {
            if (isEditStudent.value) {
                await studentApi.update(editingStudentId.value, {
                    name: studentForm.name,
                    class_name: studentForm.class_name
                })
                ElMessage.success('学生信息更新成功')
            } else {
                // 创建学生并关联学期
                const student = await studentApi.create({
                    student_id: studentForm.student_id,
                    name: studentForm.name,
                    class_name: studentForm.class_name
                })
                // 设置年级
                await studentApi.setGrade(student.id, {
                    semester_id: filters.semesterId,
                    grade: studentForm.grade
                })
                ElMessage.success('学生添加成功')
            }
            studentDialogVisible.value = false
            fetchStudents()
        } catch (e) {
            console.error(e)
        } finally {
            submitting.value = false
        }
    })
}

const deleteStudent = async (id) => {
    try {
        await studentApi.delete(id)
        ElMessage.success('学生已删除')
        fetchStudents()
    } catch (e) {
        console.error(e)
    }
}

const selectStudent = (row) => {
    selectedStudent.value = row
}

// 导入学生
const showImportStudentDialog = () => {
    importStudentForm.semesterId = filters.semesterId
    importStudentForm.pasteContent = ''
    studentFile.value = null
    importStudentDialogVisible.value = true
}

const handleStudentFileChange = (file) => {
    studentFile.value = file.raw
}

// 下载学生导入模板
const downloadStudentTemplate = () => {
    // 创建一个简单的CSV模板
    const csvContent = '学号,姓名,班级\n2025001,张三,1班\n2025002,李四,2班'
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '学生导入模板.csv'
    link.click()
    window.URL.revokeObjectURL(url)
}

// 粘贴导入学生
const importStudentsByPaste = async () => {
    if (parsedStudents.value.length === 0) {
        ElMessage.warning('没有解析到有效的学生数据')
        return
    }

    importing.value = true
    let successCount = 0
    let failCount = 0

    try {
        for (const stu of parsedStudents.value) {
            try {
                const student = await studentApi.create({
                    student_id: stu.student_id,
                    name: stu.name,
                    class_name: stu.class_name
                })
                await studentApi.setGrade(student.id, {
                    semester_id: importStudentForm.semesterId,
                    grade: importStudentForm.grade
                })
                successCount++
            } catch (e) {
                failCount++
                console.error('导入学生失败:', stu.student_id, e)
            }
        }

        if (successCount > 0) {
            ElMessage.success(`成功导入 ${successCount} 名学生${failCount > 0 ? `，${failCount} 名失败` : ''}`)
            importStudentDialogVisible.value = false
            fetchStudents()
        } else {
            ElMessage.error('所有学生导入失败，请检查数据格式')
        }
    } finally {
        importing.value = false
    }
}

const importStudents = async () => {
    if (!importStudentForm.semesterId || !importStudentForm.grade) {
        ElMessage.warning('请选择学期和年级')
        return
    }

    if (importStudentTab.value === 'paste') {
        // 粘贴导入
        await importStudentsByPaste()
    } else {
        // Excel导入
        if (!studentFile.value) {
            ElMessage.warning('请选择文件')
            return
        }
        importing.value = true
        try {
            const result = await studentApi.batchImport(
                importStudentForm.semesterId,
                importStudentForm.grade,
                studentFile.value
            )
            ElMessage.success(result.message)
            importStudentDialogVisible.value = false
            fetchStudents()
        } catch (e) {
            console.error(e)
        } finally {
            importing.value = false
        }
    }
}

// 导入成绩
const showImportGradeDialog = () => {
    importGradeForm.semesterId = filters.semesterId
    importGradeForm.examType = filters.examType || 'monthly_1'
    pasteGradeContent.value = ''
    gradeFile.value = null
    importGradeDialogVisible.value = true
}

const handleGradeFileChange = (file) => {
    gradeFile.value = file.raw
}

// 下载成绩导入模板
const downloadGradeTemplate = () => {
    // 根据当前科目列表生成包含表头的模板
    const headers = ['学号', ...subjects]
    const csvContent = headers.join(',') + '\n2025001,' + subjects.map(() => '90').join(',')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `成绩导入模板_${importGradeForm.examType}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
}

// 粘贴导入成绩
const importGradesByPaste = async () => {
    if (parsedGrades.value.length === 0) {
        ElMessage.warning('没有解析到有效的成绩数据')
        return
    }

    importing.value = true
    try {
        const batchData = {
            semester_id: importGradeForm.semesterId,
            grades: []
        }

        parsedGrades.value.forEach(data => {
            subjects.forEach(sub => {
                if (data[sub] !== undefined && data[sub] !== null) {
                    batchData.grades.push({
                        student_id_str: data.student_id,
                        exam_type: importGradeForm.examType,
                        subject: sub,
                        score: data[sub],
                        total_score: importGradeForm.subjectTotals[sub] || 100
                    })
                }
            })
        })

        if (batchData.grades.length === 0) {
            ElMessage.warning('没有解析到可导入的成绩记录')
            return
        }

        const result = await gradeApi.batch(batchData)
        ElMessage.success(result.message)
        if (result.errors && result.errors.length > 0) {
            importErrors.value = result.errors
            importErrorsVisible.value = true
        }

        importGradeDialogVisible.value = false
        fetchGrades()
    } catch (e) {
        console.error(e)
    } finally {
        importing.value = false
    }
}

const importGrades = async () => {
    if (!importGradeForm.semesterId || !importGradeForm.examType) {
        ElMessage.warning('请选择学期和考试类型')
        return
    }

    if (importGradeTab.value === 'paste') {
        await importGradesByPaste()
    } else {
        if (!gradeFile.value) {
            ElMessage.warning('请选择文件')
            return
        }
        importing.value = true
        try {
            // 将满分配置传入后端
            const subjectTotalsJson = JSON.stringify(importGradeForm.subjectTotals)
            const result = await gradeApi.batchImport(
                importGradeForm.semesterId,
                importGradeForm.examType,
                gradeFile.value,
                subjectTotalsJson
            )
            ElMessage.success(result.message)
            // 显示错误详情
            if (result.errors && result.errors.length > 0) {
                importErrors.value = result.errors
                importErrorsVisible.value = true
            }
            importGradeDialogVisible.value = false
            fetchGrades()
        } catch (e) {
            console.error(e)
        } finally {
            importing.value = false
        }
    }
}

// 导入成绩 - 当考试类型改变时的处理（allow-create 时自定义输入直接使用）
const onImportExamTypeChange = () => {}

// 批量删除成绩
const batchDeleteGrades = async () => {
    if (!filters.semesterId || !filters.examType) {
        ElMessage.warning('请先选择学期和考试类型')
        return
    }

    try {
        const result = await gradeApi.batchDelete(filters.semesterId, filters.examType)
        ElMessage.success(result.message)
        fetchGrades()
    } catch (e) {
        console.error(e)
    }
}

// 考试类型映射（预定义 + 动态扩展）
const examTypeMap = {
    'monthly_1': '月考1',
    'midterm': '期中考试',
    'monthly_2': '月考2',
    'monthly_3': '月考3',
    'final': '期末考试',
    'mock_1': '一模',
    'mock_2': '二模',
    'mock_3': '三模',
    'diagnostic': '诊断性考试',
    'joint_mock': '联合模拟'
}

// 将考试类型值转为显示标签（支持自定义类型直接显示原值）
const getExamTypeLabel = (examType) => examTypeMap[examType] || examType

const viewAllGrades = async (student) => {
    allGradesStudent.value = student
    allGradesData.value = []

    try {
        // 获取该学生所有成绩
        const allGrades = await gradeApi.getAll({ student_id: student.id })

        // 按学期和考试类型分组
        const grouped = {}
        allGrades.forEach(g => {
            const key = `${g.semester_id}_${g.exam_type}`
            if (!grouped[key]) {
                const semester = semesters.value.find(s => s.id === g.semester_id)
                grouped[key] = {
                    semester_id: g.semester_id,
                    semester_name: semester ? semester.name : `学期${g.semester_id}`,
                    exam_type: g.exam_type,
                    exam_type_label: getExamTypeLabel(g.exam_type),
                    total: 0
                }
                subjects.forEach(sub => grouped[key][sub] = '-')
            }
            grouped[key][g.subject] = g.score
            if (typeof g.score === 'number') {
                grouped[key].total += g.score
            }
        })

        // 转为数组并排序
        allGradesData.value = Object.values(grouped).sort((a, b) => {
            if (a.semester_id !== b.semester_id) return b.semester_id - a.semester_id
            return a.exam_type.localeCompare(b.exam_type)
        })

        allGradesDialogVisible.value = true
    } catch (e) {
        console.error(e)
    }
}

// 成绩录入
const showGradeEntryDialog = () => {
    if (selectedStudent.value) {
        openGradeEntry(selectedStudent.value)
    }
}

const openGradeEntry = (student) => {
    gradeEntryStudent.value = student
    gradeForm.examType = filters.examType || 'monthly_1'
    initGradeForm()

    // 加载已有成绩
    subjects.forEach(s => {
        const existing = grades.value.find(
            g => g.student_id === student.id && g.subject === s && g.exam_type === gradeForm.examType
        )
        if (existing) {
            gradeForm.scores[s] = existing.score
            gradeForm.totals[s] = existing.total_score
        }
    })

    gradeDialogVisible.value = true
}

// 切换考试类型时重新加载成绩
const loadStudentExamGrades = async () => {
    if (!gradeEntryStudent.value) return

    // 重置成绩表单
    initGradeForm()

    // 从服务器获取该学生该考试类型的成绩
    try {
        const studentGrades = await gradeApi.getAll({
            student_id: gradeEntryStudent.value.id,
            semester_id: filters.semesterId,
            exam_type: gradeForm.examType
        })

        // 填充已有成绩
        studentGrades.forEach(g => {
            if (subjects.includes(g.subject)) {
                gradeForm.scores[g.subject] = g.score
                gradeForm.totals[g.subject] = g.total_score
            }
        })
    } catch (e) {
        console.error(e)
    }
}

const submitGrades = async () => {
    if (!gradeEntryStudent.value || !filters.semesterId) return
    submitting.value = true
    try {
        // 使用 batch API 实现 upsert，避免重复录入时报错
        const batchData = {
            semester_id: filters.semesterId,
            grades: []
        }
        for (const subject of subjects) {
            const score = gradeForm.scores[subject]
            if (score === null || score === undefined) continue
            batchData.grades.push({
                student_id_str: gradeEntryStudent.value.student_id,
                exam_type: gradeForm.examType,
                subject: subject,
                score: score,
                total_score: gradeForm.totals[subject] || 100
            })
        }
        if (batchData.grades.length === 0) {
            ElMessage.warning('请至少录入一门科目的成绩')
            return
        }
        await gradeApi.batch(batchData)
        ElMessage.success('成绩保存成功')
        gradeDialogVisible.value = false
        fetchGrades()
    } catch (e) {
        console.error(e)
        ElMessage.error('保存失败，请重试')
    } finally {
        submitting.value = false
    }
}

// 初始化
onMounted(async () => {
    await fetchSemesters()
    fetchGrades()
    initGradeForm()
})
</script>

<style scoped>
.grade-management {
    max-width: 1400px;
}

.filter-card {
    margin-bottom: 16px;
}

.filter-form {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-header span {
    font-size: 16px;
    font-weight: 500;
}

.grade-entry-header {
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 8px;
    font-weight: 500;
}

/* 导入对话框样式 */
.import-tabs {
    margin-top: 16px;
}

.import-section {
    padding: 8px 0;
}

.download-btn {
    margin-bottom: 16px;
}

.upload-area {
    width: 100%;
}

.paste-tip {
    margin-bottom: 12px;
}

.paste-textarea {
    margin-bottom: 12px;
}

.paste-preview {
    margin-top: 12px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
}

.more-hint {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
}

.action-buttons {
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
}

.field-hint {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.4;
}

.subject-totals-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    width: 100%;
}

.total-grid-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    background: #f5f7fa;
    border-radius: 6px;
}

.total-label {
    font-size: 13px;
    color: #606266;
    min-width: 28px;
    font-weight: 500;
}

.error-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;
    color: #606266;
}

.error-item:last-child {
    border-bottom: none;
}
</style>
